import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  Shield,
  ShieldCheck,
  FolderTree,
  ChevronDown,
  ChevronRight,
  Server,
  Layers,
  AlertTriangle,
  Circle,
  Power,
  PowerOff,
  Rocket,
  Scan,
} from "lucide-react";
import {
  environmentsApi,
  serverComponentsApi,
  scansApi,
  Environment,
  EnvironmentServiceHierarchy,
  EnvironmentServerHierarchy,
  ServerComponentDetail,
  Scan as ScanType,
  ScanResult,
} from "@/api/client";
import DeploymentDetailsModal from "@/components/DeploymentDetailsModal";
import { formatDate, cn, titleCase } from "@/lib/utils";
import GroupSelector, { GroupSelectField } from "@/components/GroupSelector";

export default function Environments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [editingEnv, setEditingEnv] = useState<Environment | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [groupFilter, setGroupFilter] = useState<string | null>(
    searchParams.get("group"),
  );
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [expandedEnvId, setExpandedEnvId] = useState<string | null>(null);
  const [selectedScanId, setSelectedScanId] = useState<string | null>(null);
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<
    string | null
  >(null);

  // Sync groupFilter with URL
  useEffect(() => {
    const urlGroup = searchParams.get("group");
    if (urlGroup !== groupFilter) {
      setGroupFilter(urlGroup);
    }
  }, [searchParams]);

  // Update URL when filter changes
  const handleGroupFilterChange = (value: string | null) => {
    setGroupFilter(value);
    if (value) {
      setSearchParams({ group: value });
    } else {
      setSearchParams({});
    }
  };

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["environments", groupFilter],
    queryFn: () =>
      environmentsApi.list(
        groupFilter
          ? {
              group: groupFilter === "__ungrouped__" ? "" : groupFilter,
              ungrouped: groupFilter === "__ungrouped__",
            }
          : undefined,
      ),
  });

  // Query for scan details when modal is open
  const { data: scanDetails, isLoading: scanLoading } = useQuery({
    queryKey: ["scan", selectedScanId],
    queryFn: () => (selectedScanId ? scansApi.get(selectedScanId) : null),
    enabled: !!selectedScanId,
  });

  const createMutation = useMutation({
    mutationKey: ["createEnvironment"],
    mutationFn: environmentsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["environments"] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      setShowForm(false);
      setSelectedGroupId(null);
      setFormError(null);
    },
  });

  const updateMutation = useMutation({
    mutationKey: ["updateEnvironment"],
    mutationFn: ({
      id,
      ...data
    }: { id: string } & Parameters<typeof environmentsApi.update>[1]) =>
      environmentsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["environments"] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      setEditingEnv(null);
      setSelectedGroupId(null);
      setFormError(null);
    },
  });

  const deleteMutation = useMutation({
    mutationKey: ["deleteEnvironment"],
    mutationFn: environmentsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["environments"] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const envData = {
      name: formData.get("name") as string,
      slug: (formData.get("slug") as string) || undefined,
      description: formData.get("description") as string,
      is_production: formData.get("is_production") === "on",
      approval_required: formData.get("approval_required") === "on",
      group_id: selectedGroupId || undefined,
    };

    if (editingEnv) {
      updateMutation.mutate({ id: editingEnv.id, ...envData });
    } else {
      createMutation.mutate(envData);
    }
  };

  const handleEdit = (env: Environment) => {
    setEditingEnv(env);
    setSelectedGroupId(env.group_id || null);
    // Scroll to form and focus name input after render
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      nameInputRef.current?.focus();
    }, 100);
  };

  const handleDelete = (id: string, name: string) => {
    if (
      confirm(
        `Are you sure you want to delete "${name}"? This will also delete all deployment history for this environment.`,
      )
    ) {
      deleteMutation.mutate(id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingEnv(null);
    setSelectedGroupId(null);
    setFormError(null);
  };

  const toggleExpanded = (envId: string) => {
    setExpandedEnvId(expandedEnvId === envId ? null : envId);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Environments</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your deployment environments and view deployed services
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingEnv(null);
            setSelectedGroupId(null);
          }}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Environment
        </button>
      </div>

      {/* Group Filter */}
      <div className="mt-4">
        <GroupSelector
          value={groupFilter}
          onChange={handleGroupFilterChange}
          includeAll={true}
          includeUngrouped={true}
          className="max-w-xs"
        />
      </div>

      {/* Form */}
      {(showForm || editingEnv) && (
        <div ref={formRef} className="mt-6 card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {editingEnv ? "Edit Environment" : "Add Environment"}
          </h2>
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{formError}</span>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="label">
                  Name
                </label>
                <input
                  ref={nameInputRef}
                  type="text"
                  name="name"
                  id="name"
                  className="input"
                  placeholder="Production"
                  defaultValue={editingEnv?.name}
                  required
                />
              </div>
              <div>
                <label htmlFor="slug" className="label">
                  Slug (optional)
                </label>
                <input
                  type="text"
                  name="slug"
                  id="slug"
                  className="input"
                  placeholder="prod"
                  defaultValue={editingEnv?.slug}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Auto-generated from name if empty
                </p>
              </div>
            </div>

            <div>
              <label htmlFor="description" className="label">
                Description (optional)
              </label>
              <textarea
                name="description"
                id="description"
                rows={2}
                className="input"
                placeholder="Production environment for live customers"
                defaultValue={editingEnv?.description}
              />
            </div>

            <GroupSelectField
              value={selectedGroupId}
              onChange={setSelectedGroupId}
            />

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="is_production"
                  id="is_production"
                  className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  defaultChecked={editingEnv?.is_production}
                />
                <label
                  htmlFor="is_production"
                  className="text-sm text-gray-700"
                >
                  This is a production environment
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6">
                Displays a "Production" badge to indicate this environment
                serves live traffic
              </p>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="approval_required"
                  id="approval_required"
                  className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  defaultChecked={editingEnv?.approval_required}
                />
                <label
                  htmlFor="approval_required"
                  className="text-sm text-gray-700"
                >
                  Require approval for deployments
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6">
                Deployments to this environment will require approval before
                proceeding
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending
                  ? "Saving..."
                  : "Save Environment"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Environments List */}
      <div className="mt-6 space-y-4">
        {isLoading ? (
          <div className="text-gray-500">Loading...</div>
        ) : (data?.environments ?? []).length === 0 ? (
          <div className="card text-center py-8">
            <p className="text-gray-500">
              {groupFilter
                ? "No environments in this group. Add one or change the filter."
                : "No environments yet. Add your first environment to get started."}
            </p>
          </div>
        ) : (
          // Sort environments: in-path first (by sdlc_order descending, PROD at top), then off-path
          [...(data?.environments ?? [])]
            .sort((a, b) => {
              // In-path environments come first
              if (a.sdlc_in_path && !b.sdlc_in_path) return -1;
              if (!a.sdlc_in_path && b.sdlc_in_path) return 1;

              // Both in-path: sort by sdlc_order descending (higher order = higher in list, PROD at top)
              if (a.sdlc_in_path && b.sdlc_in_path) {
                const orderA = a.sdlc_order ?? 0;
                const orderB = b.sdlc_order ?? 0;
                return orderB - orderA;
              }

              // Both off-path: sort alphabetically by name
              return a.name.localeCompare(b.name);
            })
            .map((env) => (
              <div key={env.id} className="card">
                <div className="flex items-start justify-between">
                  <div
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => toggleExpanded(env.id)}
                  >
                    {expandedEnvId === env.id ? (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                    {env.is_production ? (
                      <ShieldCheck className="h-5 w-5 text-red-500" />
                    ) : (
                      <Shield className="h-5 w-5 text-gray-400" />
                    )}
                    <div>
                      <h3 className="font-medium text-gray-900">{env.name}</h3>
                      <p className="text-sm text-gray-500">{env.slug}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEdit(env)}
                      className="p-2 text-gray-400 hover:text-gray-600"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(env.id, env.name)}
                      className="p-2 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {env.description && (
                  <p className="mt-3 text-sm text-gray-600">
                    {env.description}
                  </p>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {env.is_production && (
                    <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                      Production
                    </span>
                  )}
                  {env.approval_required && (
                    <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                      Approval Required
                    </span>
                  )}
                  {env.group_name && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-medium text-brand-800">
                      <FolderTree className="h-3 w-3" />
                      {env.group_name}
                    </span>
                  )}
                </div>

                {/* Expanded Section for Services */}
                {expandedEnvId === env.id && (
                  <EnvironmentServiceList
                    environmentId={env.id}
                    onSelectScan={setSelectedScanId}
                    onSelectDeployment={setSelectedDeploymentId}
                  />
                )}

                <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-400">
                  Created {formatDate(env.created_at)}
                </div>
              </div>
            ))
        )}
      </div>

      {/* Scan Details Modal */}
      {selectedScanId && (
        <ScanDetailsModal
          scan={scanDetails?.scan}
          results={scanDetails?.results || []}
          isLoading={scanLoading}
          onClose={() => setSelectedScanId(null)}
        />
      )}

      {/* Deployment Details Modal */}
      {selectedDeploymentId && (
        <DeploymentDetailsModal
          deploymentId={selectedDeploymentId}
          onClose={() => setSelectedDeploymentId(null)}
        />
      )}
    </div>
  );
}

// Scan Details Modal Component
function ScanDetailsModal({
  scan,
  results,
  isLoading,
  onClose,
}: {
  scan?: ScanType;
  results: ScanResult[];
  isLoading: boolean;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[80vh] overflow-y-auto scrollbar-hide"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoading ? (
          <div className="p-6 text-center text-gray-500">
            Loading scan details...
          </div>
        ) : !scan ? (
          <div className="p-6 text-center text-gray-500">Scan not found</div>
        ) : (
          <>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Scan className="h-5 w-5 text-brand-600" />
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {scan.scan_type === "initial"
                        ? "Initial Scan"
                        : scan.scan_type === "daily"
                          ? "Daily Scan"
                          : "Scan"}{" "}
                      Details
                    </h2>
                    <p className="text-sm text-gray-500">
                      {scan.environment_name} • {formatDate(scan.started_at)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Summary */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {scan.servers_scanned}
                  </div>
                  <div className="text-xs text-gray-500">Servers</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {scan.components_checked}
                  </div>
                  <div className="text-xs text-gray-500">Components</div>
                </div>
                <div>
                  <div
                    className={`text-2xl font-bold ${scan.drift_count > 0 ? "text-orange-600" : "text-green-600"}`}
                  >
                    {scan.drift_count}
                  </div>
                  <div className="text-xs text-gray-500">Drift</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600 capitalize">
                    {scan.status}
                  </div>
                  <div className="text-xs text-gray-500">Status</div>
                </div>
              </div>
              {scan.source_url && (
                <div className="mt-3 text-center">
                  <a
                    href={scan.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    View in {scan.source || "Source"} →
                  </a>
                </div>
              )}
            </div>

            {/* Results Table */}
            <div className="px-6 py-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Scan Results
              </h3>
              {results.length === 0 ? (
                <p className="text-sm text-gray-500">No results recorded</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Component
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Service
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Version
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {results.map((result) => (
                        <tr
                          key={result.id}
                          className={result.is_drift ? "bg-orange-50" : ""}
                        >
                          <td className="px-3 py-2">
                            <div className="text-gray-900">
                              {result.component_name ||
                                result.component_identifier}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-gray-500">
                            {result.service_name
                              ? titleCase(result.service_name)
                              : "-"}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-900">
                            {result.detected_version}
                          </td>
                          <td className="px-3 py-2">
                            {result.is_drift ? (
                              <span className="inline-flex items-center gap-1 text-orange-600">
                                <AlertTriangle className="h-3 w-3" />
                                Drift
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-green-600">
                                <Power className="h-3 w-3" />
                                OK
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Component to display service hierarchy for an environment
function EnvironmentServiceList({
  environmentId,
  onSelectScan,
  onSelectDeployment,
}: {
  environmentId: string;
  onSelectScan: (scanId: string) => void;
  onSelectDeployment: (deploymentId: string) => void;
}) {
  const [expandedServices, setExpandedServices] = useState<Set<string>>(
    new Set(),
  );
  const [expandedServers, setExpandedServers] = useState<Set<string>>(
    new Set(),
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ["environment-services", environmentId],
    queryFn: () => environmentsApi.getServiceHierarchy(environmentId),
  });

  const toggleService = (serviceId: string) => {
    setExpandedServices((prev) => {
      const next = new Set(prev);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  };

  const toggleServer = (serverId: string) => {
    setExpandedServers((prev) => {
      const next = new Set(prev);
      if (next.has(serverId)) {
        next.delete(serverId);
      } else {
        next.add(serverId);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="mt-6 pt-6 border-t border-gray-200">
        <p className="text-sm text-gray-500">Loading services...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 pt-6 border-t border-gray-200">
        <p className="text-sm text-red-500">Failed to load services</p>
      </div>
    );
  }

  const services = data?.services ?? [];

  if (services.length === 0) {
    return (
      <div className="mt-6 pt-6 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          No services deployed to this environment yet.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
      <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
        <Layers className="h-4 w-4 text-gray-500" />
        Deployed Services
      </h4>

      {services.map((service) => (
        <ServiceCard
          key={service.service_id}
          service={service}
          isExpanded={expandedServices.has(service.service_id)}
          onToggle={() => toggleService(service.service_id)}
          expandedServers={expandedServers}
          onToggleServer={toggleServer}
          onSelectScan={onSelectScan}
          onSelectDeployment={onSelectDeployment}
        />
      ))}
    </div>
  );
}

// Service card with expandable servers
function ServiceCard({
  service,
  isExpanded,
  onToggle,
  expandedServers,
  onToggleServer,
  onSelectScan,
  onSelectDeployment,
}: {
  service: EnvironmentServiceHierarchy;
  isExpanded: boolean;
  onToggle: () => void;
  expandedServers: Set<string>;
  onToggleServer: (serverId: string) => void;
  onSelectScan: (scanId: string) => void;
  onSelectDeployment: (deploymentId: string) => void;
}) {
  const hasIssues =
    service.drift_count > 0 ||
    service.servers.some((s) => s.components.some((c) => c.is_unexpected_stop));
  const unexpectedStopCount = service.servers.reduce(
    (acc, s) => acc + s.components.filter((c) => c.is_unexpected_stop).length,
    0,
  );

  return (
    <div className="bg-gray-50 rounded-lg">
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-100 rounded-lg"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          )}
          <Layers className="h-4 w-4 text-brand-600" />
          <span className="text-sm font-medium text-gray-900">
            {titleCase(service.service_name)}
          </span>
          <span className="text-xs text-gray-500">
            ({service.service_slug})
          </span>
        </div>
        <div className="flex items-center gap-2">
          {hasIssues && (
            <>
              {service.drift_count > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-orange-100 text-orange-700">
                  <AlertTriangle className="h-3 w-3" />
                  {service.drift_count} drift
                </span>
              )}
              {unexpectedStopCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700">
                  <PowerOff className="h-3 w-3" />
                  {unexpectedStopCount} stopped
                </span>
              )}
            </>
          )}
          <span className="text-xs text-gray-500">
            {service.servers.length} server
            {service.servers.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="px-3 pb-3 space-y-2">
          {service.servers.map((server) => (
            <ServerSection
              key={server.server_id}
              server={server}
              isExpanded={expandedServers.has(server.server_id)}
              onToggle={() => onToggleServer(server.server_id)}
              onSelectScan={onSelectScan}
              onSelectDeployment={onSelectDeployment}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Server section with expandable components
function ServerSection({
  server,
  isExpanded,
  onToggle,
  onSelectScan,
  onSelectDeployment,
}: {
  server: EnvironmentServerHierarchy;
  isExpanded: boolean;
  onToggle: () => void;
  onSelectScan: (scanId: string) => void;
  onSelectDeployment: (deploymentId: string) => void;
}) {
  const hasIssues =
    server.drift_count > 0 ||
    server.components.some((c) => c.is_unexpected_stop);
  const unexpectedStopCount = server.components.filter(
    (c) => c.is_unexpected_stop,
  ).length;

  return (
    <div className="ml-6 bg-white rounded-lg border border-gray-200">
      <div
        className="flex items-center justify-between p-2 cursor-pointer hover:bg-gray-50 rounded-t-lg"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-3 w-3 text-gray-400" />
          ) : (
            <ChevronRight className="h-3 w-3 text-gray-400" />
          )}
          <Server className="h-3 w-3 text-gray-500" />
          <span className="text-xs font-medium text-gray-700">
            {server.hostname}
          </span>
          {server.ip_address && (
            <span className="text-xs text-gray-400">({server.ip_address})</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasIssues && (
            <>
              {server.drift_count > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded bg-orange-100 text-orange-700">
                  {server.drift_count} drift
                </span>
              )}
              {unexpectedStopCount > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700">
                  {unexpectedStopCount} stopped
                </span>
              )}
            </>
          )}
          <span className="text-xs text-gray-400">
            {server.components.length} component
            {server.components.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {isExpanded && server.components.length > 0 && (
        <ComponentsTable
          components={server.components}
          onSelectScan={onSelectScan}
          onSelectDeployment={onSelectDeployment}
        />
      )}
    </div>
  );
}

// Components table
function ComponentsTable({
  components,
  onSelectScan,
  onSelectDeployment,
}: {
  components: ServerComponentDetail[];
  onSelectScan: (scanId: string) => void;
  onSelectDeployment: (deploymentId: string) => void;
}) {
  const queryClient = useQueryClient();

  const updateExpectedStoppedMutation = useMutation({
    mutationFn: ({
      id,
      expectedStopped,
    }: {
      id: string;
      expectedStopped: boolean;
    }) => serverComponentsApi.updateExpectedStopped(id, expectedStopped),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["environment-services"] });
    },
  });

  return (
    <div className="border-t border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Component
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Version
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Version Set
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Status
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {components.map((component) => (
            <tr key={component.id} className="hover:bg-gray-50">
              <td className="px-3 py-2">
                <div>
                  <div className="text-xs font-medium text-gray-900">
                    {component.component_name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {component.identifier}
                  </div>
                </div>
              </td>
              <td className="px-3 py-2">
                <VersionDisplay component={component} />
              </td>
              <td className="px-3 py-2">
                <VersionSourceDisplay
                  component={component}
                  onSelectScan={onSelectScan}
                  onSelectDeployment={onSelectDeployment}
                />
              </td>
              <td className="px-3 py-2">
                <StatusDisplay component={component} />
              </td>
              <td className="px-3 py-2 text-right">
                <ExpectedOffToggle
                  component={component}
                  onToggle={(expectedStopped) =>
                    updateExpectedStoppedMutation.mutate({
                      id: component.id,
                      expectedStopped,
                    })
                  }
                  isLoading={updateExpectedStoppedMutation.isPending}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Version display - shows expected version, shows drift indicator if actual differs
function VersionDisplay({ component }: { component: ServerComponentDetail }) {
  if (!component.expected_version && !component.actual_version) {
    return <span className="text-xs text-gray-400">-</span>;
  }

  return (
    <div className="space-y-0.5">
      <div className="text-xs font-mono text-gray-900">
        {component.expected_version || component.actual_version || "-"}
      </div>
      {component.is_drift && component.actual_version && (
        <div className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 text-orange-500" />
          <span className="text-xs font-mono text-orange-600">
            Actual: {component.actual_version}
          </span>
        </div>
      )}
    </div>
  );
}

// Version source display - shows when/how version was set with link to source
function VersionSourceDisplay({
  component,
  onSelectScan,
  onSelectDeployment,
}: {
  component: ServerComponentDetail;
  onSelectScan: (scanId: string) => void;
  onSelectDeployment: (deploymentId: string) => void;
}) {
  if (!component.version_set_at) {
    return <span className="text-xs text-gray-400">-</span>;
  }

  const date = formatDate(component.version_set_at);

  // If set by deployment - open modal instead of navigating
  if (component.deployment_id) {
    return (
      <div className="space-y-0.5">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSelectDeployment(component.deployment_id!);
          }}
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer bg-transparent border-none p-0"
        >
          <Rocket className="h-3 w-3" />
          Deployment
        </button>
        <div className="text-xs text-gray-500">{date}</div>
      </div>
    );
  }

  // If set by initial scan - open modal instead of navigating
  if (component.baseline_scan_id) {
    return (
      <div className="space-y-0.5">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSelectScan(component.baseline_scan_id!);
          }}
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer bg-transparent border-none p-0"
        >
          <Scan className="h-3 w-3" />
          Initial Scan
        </button>
        <div className="text-xs text-gray-500">{date}</div>
      </div>
    );
  }

  // Fallback - just show date and source type
  return (
    <div className="space-y-0.5">
      <div className="text-xs text-gray-600">
        {component.version_set_by === "initial_scan"
          ? "Initial Scan"
          : component.version_set_by || "Unknown"}
      </div>
      <div className="text-xs text-gray-500">{date}</div>
    </div>
  );
}

// Status display with color coding
function StatusDisplay({ component }: { component: ServerComponentDetail }) {
  const status = component.status?.toLowerCase();

  if (!status) {
    return <span className="text-xs text-gray-400">-</span>;
  }

  // Running status
  if (status === "running") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
        <Power className="h-3 w-3" />
        Running
      </span>
    );
  }

  // Stopped status
  if (status === "stopped" || status === "exited") {
    if (component.expected_stopped) {
      // Expected to be stopped - gray/neutral
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500">
          <Circle className="h-3 w-3" />
          Stopped (Expected)
        </span>
      );
    } else {
      // Unexpected stop - red alert
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700">
          <AlertTriangle className="h-3 w-3" />
          Stopped (Unexpected!)
        </span>
      );
    }
  }

  // Other statuses
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600">
      <Circle className="h-3 w-3" />
      {status}
    </span>
  );
}

// Toggle for marking a component as "expected off"
function ExpectedOffToggle({
  component,
  onToggle,
  isLoading,
}: {
  component: ServerComponentDetail;
  onToggle: (expectedStopped: boolean) => void;
  isLoading: boolean;
}) {
  const status = component.status?.toLowerCase();
  const isStopped = status === "stopped" || status === "exited";

  // Only show toggle for stopped components
  if (!isStopped) {
    return null;
  }

  return (
    <button
      onClick={() => onToggle(!component.expected_stopped)}
      disabled={isLoading}
      className={cn(
        "text-xs px-2 py-1 rounded transition-colors",
        component.expected_stopped
          ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
          : "bg-yellow-100 text-yellow-700 hover:bg-yellow-200",
        isLoading && "opacity-50 cursor-not-allowed",
      )}
      title={
        component.expected_stopped
          ? "Click to mark as should be running"
          : "Click to mark as expected off"
      }
    >
      {isLoading
        ? "..."
        : component.expected_stopped
          ? "Mark as should run"
          : "Mark as expected off"}
    </button>
  );
}
